import React, { useRef, useState, ChangeEvent } from 'react';
import { uploadLogo, deleteLogo } from '../api';
import useImageBrightness, { ImageTone } from '../useImageBrightness';
import detailMessage from '../detailMessage';
import { useLang } from '../LangContext';

interface Props {
  token: string;
  initialLogoUrl?: string | null;
  onChange?: (url: string | null) => void;
}

const LogoUploadForm = ({ token, initialLogoUrl, onChange }: Props) => {
  const { t } = useLang();
  const [logoUrl, setLogoUrl] = useState<string | null>(initialLogoUrl || null);
  const [imgError, setImgError] = useState(false);
  const activeUrl = logoUrl && !imgError ? logoUrl : null;
  const imageTone: ImageTone = useImageBrightness(activeUrl);
  const previewBg = imageTone === 'light' ? '#374151' : imageTone === 'dark' ? '#f1f5f9' : undefined;
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const clearFeedback = () => { setError(''); setSuccess(''); };

  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    clearFeedback();
    setUploading(true);
    try {
      const res = await uploadLogo(file, token);
      setLogoUrl(res.logo_url);
      setImgError(false);
      setSuccess(t.logoUpdated);
      onChange?.(res.logo_url);
      setTimeout(() => setSuccess(''), 4000);
    } catch (err) {
      setError(detailMessage(err, t.logoUploadFailed));
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = async () => {
    clearFeedback();
    setUploading(true);
    try {
      await deleteLogo(token);
      setLogoUrl(null);
      onChange?.(null);
    } catch {
      setError(t.logoRemoveFailed);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="profile-section">
      <h4 className="profile-section-title">{t.profilePictureHeading}</h4>

      {error && <div className="error-message"><strong>{t.errorLabel2}</strong> {error}</div>}
      {success && <div className="success-message"><strong>✓</strong> {success}</div>}

      <div className="logo-upload-row">
        <div className="logo-preview-wrap" style={previewBg ? { background: previewBg } : undefined}>
          {logoUrl && !imgError ? (
            <img
              src={logoUrl}
              alt={t.logoAlt}
              className="logo-preview-img"
              onError={() => setImgError(true)}
            />
          ) : (
            <span className="logo-preview-placeholder" aria-hidden="true">
              <svg viewBox="0 0 24 24" width="32" height="32" fill="currentColor">
                <path d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10Zm0 2c-4.42 0-8 2.69-8 6v2h16v-2c0-3.31-3.58-6-8-6Z" />
              </svg>
            </span>
          )}
        </div>

        <div className="logo-upload-actions">
          <button
            type="button"
            className="submit-button"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? t.uploadingButton : logoUrl ? t.changePictureButton : t.uploadPictureButton}
          </button>
          {logoUrl && (
            <button
              type="button"
              className="secondary-button"
              disabled={uploading}
              onClick={handleRemove}
            >
              {t.removePictureButton}
            </button>
          )}
        </div>
      </div>

      <p className="field-hint">{t.logoHint}</p>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        style={{ display: 'none' }}
        onChange={(e: ChangeEvent<HTMLInputElement>) => handleFile(e.target.files?.[0])}
      />
    </div>
  );
};

export default LogoUploadForm;
